import "./style.scss";

import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { CircularProgress, Collapse } from "@mui/material";
import type { INewStatementForm } from "components/PartnerStatementsSettings/types";
import ButtonCollapse from "components/shared/ButtonCollapse";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
    fetchGoogleSheets,
    setCollapsedSpreadsheet,
    setGoogleSheets,
    setSelectedSheet,
} from "store/partnerStatementsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import spreadsheetIcon from "../../../../../assets/img/spreadsheet-icon.png";
import SheetRow from "./SheetRow";
/**
 * This component render a displayed row of a Google Spreadsheet
 */
interface ISpreadSheetRow {
    modifiedTime: string;
    spreadsheetId: string;
    spreadsheetName: string;
    integrationId: number;
    /**
     * filtered google sheets (array of sheets)
     */
    filteredSheets: any;
    mode: "edit" | "new";
}

const SpreadSheetRow = ({
    modifiedTime,
    spreadsheetId,
    spreadsheetName,
    integrationId,
    filteredSheets,
    mode,
}: ISpreadSheetRow) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { collapsedSpreadsheet, fetchGoogleSheetsStatus } = useAppSelector((state) => state.partnerStatements);
    const isCollapsed = collapsedSpreadsheet.id === spreadsheetId;

    const { setValue, resetField } = useFormContext<INewStatementForm>();
    const { paginatedData: paginatedSheets, paginatedElRef } = useInfiniteScroll(filteredSheets, isCollapsed);

    const onCollapseSpreadsheetRow = () => {
        if (isCollapsed) {
            dispatch(setGoogleSheets([]));
            dispatch(setCollapsedSpreadsheet({ id: "", name: "" }));
            dispatch(setSelectedSheet({ id: null, name: "" }));
            resetField("spreadsheetId");
            resetField("searchSheet");
        } else {
            dispatch(setCollapsedSpreadsheet({ id: spreadsheetId, name: spreadsheetName }));
            dispatch(fetchGoogleSheets({ spreadsheetId: spreadsheetId, integrationId: integrationId }));
            setValue("spreadsheetId", spreadsheetId);
            resetField("searchSpreadsheet");
        }
    };

    const onSelectSheet = (sheetTitle: string, sheetId: number) => {
        dispatch(setSelectedSheet({ id: sheetId, name: sheetTitle }));
        setValue("sheetId", sheetId);
        if (mode === "new") setValue("name", sheetTitle);
    };

    return (
        <>
            <div
                className={`google-spreadsheet-row ${isCollapsed ? "selected" : ""}`}
                onClick={onCollapseSpreadsheetRow}
            >
                <div className="google-spreadsheet-row-left">
                    {isCollapsed && <ArrowBackOutlinedIcon className="undo-icon" />}
                    <img src={spreadsheetIcon} className="spreadsheet-icon" />
                    <div className="spreadsheet-title" title={spreadsheetName}>
                        <span className="spreadsheet-name">{spreadsheetName}</span>
                    </div>
                </div>
                <div className="google-spreadsheet-row-right">
                    <span className="spreadsheet-edited-date">{`${t(
                        "PartnerStatements.EditedAt",
                    )} ${modifiedTime}`}</span>
                    <ButtonCollapse collapse={isCollapsed} sx={{ color: "var(--foreground)" }} />
                </div>
            </div>
            <Collapse in={isCollapsed} timeout="auto">
                {fetchGoogleSheetsStatus === "pending" ? (
                    <div className="google-sheet-pending-status">
                        <CircularProgress className="google-sheet-pending-status-icon" />
                        <span>{t("PartnerStatements.ViewingSheets")}</span>
                    </div>
                ) : (
                    <ScrollShadowWrapper className="google-spreadsheet-sheets-list" ref={paginatedElRef}>
                        {paginatedSheets?.map((sheet: any, index) => (
                            <SheetRow
                                key={`sheet-row-${sheet.properties.id}-${index}`}
                                sheetId={sheet.properties.sheetId}
                                sheetTitle={sheet.properties.title}
                                index={index}
                                onSelectSheet={onSelectSheet}
                            />
                        ))}
                    </ScrollShadowWrapper>
                )}
            </Collapse>
        </>
    );
};

export default SpreadSheetRow;
